/**
 * Cart Store - Handles cart sidebar and AJAX operations
 *
 * Architecture:
 * - Server (lib_cart.inc.php) handles data operations
 * - AJAX submits forms without page reload
 * - UI updates dynamically with cart sidebar
 * - Animations and smooth UX
 */
export default {
  // ========================================
  // STATE
  // ========================================
  isOpen: false,
  loading: false,
  itemCount: 0,

  // Analytics integration
  analyticsEnabled: false,

  // ========================================
  // HELPERS
  // ========================================

  /** Base path for AJAX URLs — uses server's _env or falls back to '/' */
  get basePath() {
    return window._env?.platform?.path || '/';
  },

  /** Full AJAX URL for cart operations */
  get cartUrl() {
    return this.basePath + 'ajax/cart.json';
  },

  /**
   * Parse cart count from server response (handles both formats)
   * POST format: { success: true, cart: { itemCount: N, subtotal: "..." } }
   * GET format:  { items: [...], quantity: N, subtotal: { value, formatted } }
   */
  parseCartCount(data) {
    if (data.cart?.itemCount !== undefined) {
      return parseInt(data.cart.itemCount) || 0;
    }
    return parseInt(data.quantity) || 0;
  },

  // ========================================
  // INITIALIZATION
  // ========================================
  init() {
    // Sync with global state
    this.isOpen = window.cartSidebarOpen || false;

    // Initialize cart count from server-rendered value (prevents flash of wrong template)
    const component = document.querySelector('.cart-component');
    if (component?.dataset.initialCount) {
      this.itemCount = parseInt(component.dataset.initialCount) || 0;
    }

    // Check if Analytics is available
    this.analyticsEnabled = typeof window.Analytics !== 'undefined';

    // Verify cart count from AJAX (in case session changed)
    this.updateCartCount();

    // Listen for events
    this.bindEvents();

    // Intercept all "Add to Cart" forms
    this.interceptForms();
  },

  bindEvents() {
    // Listen for cart sidebar events
    document.addEventListener('cart-sidebar-opened', () => {
      this.isOpen = true;
    });

    document.addEventListener('cart-sidebar-closed', () => {
      this.isOpen = false;
    });

    document.addEventListener('cart-sidebar-toggled', () => {
      this.toggle();
    });
  },

  // ========================================
  // FORM INTERCEPTION
  // ========================================
  interceptForms() {
    // Intercept "Add to Cart" forms
    document.addEventListener('submit', e => {
      const form = e.target;

      // Check if this is an "Add to Cart" form
      if (form.name === 'buy_now_form' || form.querySelector('button[name="add_cart_product"]')) {
        e.preventDefault();
        this.addToCart(form);
      }
    });
  },

  // ========================================
  // CART OPERATIONS
  // ========================================
  async addToCart(form) {
    if (this.loading) return;

    this.loading = true;

    // Store current cart count to compare after
    const previousCount = this.itemCount;

    // Store product info for analytics (before any DOM changes)
    let productBox = null;
    if (this.analyticsEnabled) {
      productBox = document.getElementById('box-product') || form.closest('.product-card');
    }

    try {
      // Get form data
      const formData = new FormData(form);

      // Add the submit button field (required by server)
      formData.append('add_cart_product', 'true');

      // POST to cart.json — server processes cart in global includes,
      // then returns lightweight JSON (~1KB) instead of full page HTML (~100KB+)
      const response = await fetch(this.cartUrl, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const cartData = await response.json();

      // Check for server-side errors (out of stock, validation, etc.)
      if (cartData.alert) {
        if (window.Notification) {
          window.Notification.error(cartData.alert, 4000);
        }
        return;
      }

      // Update cart count from response (handles both POST and GET formats)
      this.itemCount = this.parseCartCount(cartData);
      document.dispatchEvent(new CustomEvent('cart-updated', { detail: cartData }));

      // Check if cart count actually increased
      if (this.itemCount <= previousCount) {
        // Item was not added - highlight missing options and scroll to them
        this.highlightMissingOptions(form);
        return;
      }

      // SUCCESS - Item was added!

      // Now send Analytics event (only on success)
      if (this.analyticsEnabled && productBox) {
        window.Analytics.addToCart(productBox);
      }

      // Fetch full cart data for sidebar (POST response may not include items)
      await this.refreshCart();

      // Fly-to-cart animation on all devices
      this.animateProductToCart(form);
      await new Promise(resolve => setTimeout(resolve, 600));

      this.open();

      if (window.Notification) {
        window.Notification.success(window.tx('notificationAddedToCart', 'Added to cart!'), 2000);
      }

    } catch (error) {
      console.error('[Cart] addToCart failed:', error);
      if (window.Notification) {
        window.Notification.error(window.tx('errorAddToCart', 'Failed to add to cart'), 3000);
      }
    } finally {
      this.loading = false;
    }
  },


  /**
   * Highlight missing/unselected required options and scroll to them
   */
  highlightMissingOptions(form) {
    // Find all required option groups
    const optionGroups = form.querySelectorAll('.product-option-group[data-required="true"]');
    let firstMissing = null;
    const missingOptions = [];

    optionGroups.forEach(group => {
      const optionName = group.dataset.optionName;

      // Check if any radio/checkbox is selected
      const radios = group.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      const selects = group.querySelectorAll('select');

      let hasSelection = false;

      // Check radios/checkboxes
      if (radios.length > 0) {
        hasSelection = Array.from(radios).some(r => r.checked);
      }

      // Check selects
      if (selects.length > 0) {
        hasSelection = hasSelection || Array.from(selects).some(s => s.value !== '');
      }

      if (!hasSelection) {
        missingOptions.push(optionName);

        // Add highlight animation
        this.addHighlightEffect(group);

        if (!firstMissing) {
          firstMissing = group;
        }
      }
    });

    // No missing options found — all are selected
    if (missingOptions.length === 0) return;

    // Show notification with missing option names
    if (window.Notification) {
      const message = missingOptions.length === 1
        ? window.tx('warningSelectOption', 'Please select') + ': ' + missingOptions[0]
        : window.tx('warningSelectOptions', 'Please select options') + ': ' + missingOptions.join(', ');
      window.Notification.warning(message, 4000);
    }

    // Scroll to first missing option
    if (firstMissing) {
      this.scrollToElement(firstMissing);
    }
  },

  /**
   * Add highlight/shake effect to an element
   */
  addHighlightEffect(element) {
    // Add shake animation keyframes if not exist
    if (!document.getElementById('option-highlight-keyframes')) {
      const style = document.createElement('style');
      style.id = 'option-highlight-keyframes';
      style.textContent = `
        @keyframes option-shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .option-highlight {
          animation: option-shake 0.5s ease-in-out;
        }
        .option-highlight-border {
          position: relative;
        }
        .option-highlight-border::before {
          content: '';
          position: absolute;
          inset: -2px;
          border: 2px solid var(--color-error, #ef4444);
          border-radius: 4px;
          animation: option-pulse 1s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes option-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `;
      document.head.appendChild(style);
    }

    // Add classes
    element.classList.add('option-highlight', 'option-highlight-border');

    // Remove highlight after animation
    setTimeout(() => {
      element.classList.remove('option-highlight');
    }, 500);

    // Remove border highlight after 3 seconds
    setTimeout(() => {
      element.classList.remove('option-highlight-border');
    }, 3000);
  },

  /**
   * Smooth scroll to element with offset for header
   */
  scrollToElement(element) {
    const headerHeight = 100; // Account for fixed header
    const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementTop - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    // Also focus the first input in the group after scroll
    setTimeout(() => {
      const firstInput = element.querySelector('input, select');
      if (firstInput) {
        firstInput.focus();
      }
    }, 500);
  },

  animateProductToCart(form) {
    try {
      // Find the main product image - priority order:
      // 1. Product detail page: #main-product-image
      // 2. Listing card: .product-image-wrapper img
      // 3. Quick view or other: first img in container
      let productImage = null;

      // Check if we're on product detail page
      productImage = document.getElementById('main-product-image');

      // If not found, look in listing card
      if (!productImage) {
        const productCard = form.closest('article') || form.closest('.product-card');
        if (productCard) {
          productImage = productCard.querySelector('.product-image-wrapper img') ||
            productCard.querySelector('img');
        }
      }

      if (!productImage) return;

      // Find cart icon
      const cartIcon = document.querySelector('#cart-trigger') ||
        document.querySelector('[data-cart-trigger]') ||
        document.querySelector('.cart-component button');
      if (!cartIcon) return;

      // Get positions
      const imageRect = productImage.getBoundingClientRect();
      const cartRect = cartIcon.getBoundingClientRect();

      // Calculate center points
      const startX = imageRect.left + imageRect.width / 2;
      const startY = imageRect.top + imageRect.height / 2;
      const endX = cartRect.left + cartRect.width / 2;
      const endY = cartRect.top + cartRect.height / 2;

      // Create flying element container
      const flyingContainer = document.createElement('div');
      flyingContainer.style.cssText = `
        position: fixed;
        z-index: 99999;
        pointer-events: none;
        top: ${startY}px;
        left: ${startX}px;
        transform: translate(-50%, -50%);
      `;

      // Create the flying image
      const flyingImg = document.createElement('div');
      const isMobile = window.innerWidth < 768;
      const imgSize = Math.min(imageRect.width, imageRect.height, isMobile ? 80 : 120);
      flyingImg.style.cssText = `
        width: ${imgSize}px;
        height: ${imgSize}px;
        border-radius: 50%;
        overflow: hidden;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
        border: 3px solid var(--color-accent, #c9a96e);
        background: var(--color-surface, #fff);
      `;

      // Clone image inside circle
      const imgClone = productImage.cloneNode(true);
      imgClone.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: cover;
      `;
      imgClone.removeAttribute('srcset');
      imgClone.removeAttribute('loading');
      flyingImg.appendChild(imgClone);
      flyingContainer.appendChild(flyingImg);
      document.body.appendChild(flyingContainer);

      // Calculate arc path (parabolic curve)
      const dx = endX - startX;
      const dy = endY - startY;
      const duration = isMobile ? 500 : 700;

      // Animate using requestAnimationFrame for smooth arc
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function (ease-out cubic)
        const easeOut = 1 - Math.pow(1 - progress, 3);

        // Arc calculation - higher arc at the beginning
        const arcHeight = -150 * Math.sin(progress * Math.PI);

        // Current position
        const currentX = startX + dx * easeOut;
        const currentY = startY + dy * easeOut + arcHeight;

        // Scale down as it moves
        const scale = 1 - (progress * 0.7);

        // Apply transforms
        flyingContainer.style.top = `${currentY}px`;
        flyingContainer.style.left = `${currentX}px`;
        flyingImg.style.transform = `scale(${scale}) rotate(${progress * 360}deg)`;
        flyingImg.style.opacity = `${1 - progress * 0.3}`;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Animation complete - create burst effect
          this.createBurstEffect(endX, endY);

          // Remove flying element
          if (flyingContainer.parentNode) {
            flyingContainer.parentNode.removeChild(flyingContainer);
          }

          // Pulse cart icon
          this.pulseCartIcon(cartIcon);
        }
      };

      requestAnimationFrame(animate);

    } catch (error) {
      // Animation failed silently - non-critical
    }
  },

  /**
   * Create burst/sparkle effect at destination
   */
  createBurstEffect(x, y) {
    const burst = document.createElement('div');
    burst.style.cssText = `
      position: fixed;
      z-index: 99998;
      top: ${y}px;
      left: ${x}px;
      transform: translate(-50%, -50%);
      pointer-events: none;
    `;

    // Create multiple particles
    for (let i = 0; i < 8; i++) {
      const particle = document.createElement('div');
      const angle = (i / 8) * Math.PI * 2;
      const distance = 30;

      particle.style.cssText = `
        position: absolute;
        width: 8px;
        height: 8px;
        background: var(--color-accent, #c9a96e);
        border-radius: 50%;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        animation: burst-particle 0.4s ease-out forwards;
        --tx: ${Math.cos(angle) * distance}px;
        --ty: ${Math.sin(angle) * distance}px;
      `;
      burst.appendChild(particle);
    }

    document.body.appendChild(burst);

    // Add keyframes if not exist
    if (!document.getElementById('burst-keyframes')) {
      const style = document.createElement('style');
      style.id = 'burst-keyframes';
      style.textContent = `
        @keyframes burst-particle {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) scale(0); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    // Remove burst after animation
    setTimeout(() => {
      if (burst.parentNode) burst.parentNode.removeChild(burst);
    }, 500);
  },

  /**
   * Pulse animation for cart icon
   */
  pulseCartIcon(cartIcon) {
    // Add keyframes if not exist
    if (!document.getElementById('cart-pulse-keyframes')) {
      const style = document.createElement('style');
      style.id = 'cart-pulse-keyframes';
      style.textContent = `
        @keyframes cart-pulse {
          0% { transform: scale(1); }
          30% { transform: scale(1.3); }
          60% { transform: scale(0.9); }
          100% { transform: scale(1); }
        }
      `;
      document.head.appendChild(style);
    }

    cartIcon.style.animation = 'cart-pulse 0.5s ease-in-out';
    setTimeout(() => {
      cartIcon.style.animation = '';
    }, 500);
  },

  async refreshCart() {
    try {
      // Fetch updated cart data (no-store prevents stale cached count)
      const response = await fetch(this.cartUrl, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Update cart count
      this.itemCount = parseInt(data.quantity) || 0;

      // Trigger cart updated event
      document.dispatchEvent(
        new CustomEvent('cart-updated', {
          detail: data,
        })
      );

      // Update sidebar DOM if available
      this.updateSidebarFromJson(data);
    } catch (error) {
      console.error('[Cart] refreshCart failed:', error);
    }
  },

  async updateCartCount() {
    try {
      const response = await fetch(this.cartUrl, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        cache: 'no-store',
      });

      if (response.ok) {
        const data = await response.json();
        this.itemCount = parseInt(data.quantity) || 0;
      }
    } catch (error) {
      console.error('[Cart] updateCartCount failed:', error);
    }
  },

  // ========================================
  // UI STATE MANAGEMENT
  // ========================================
  open() {
    this.isOpen = true;
    window.cartSidebarOpen = true;
    document.body.classList.add('overflow-hidden');
    document.dispatchEvent(new CustomEvent('cart-sidebar-opened'));
  },

  close() {
    this.isOpen = false;
    window.cartSidebarOpen = false;
    document.body.classList.remove('overflow-hidden');
    document.dispatchEvent(new CustomEvent('cart-sidebar-closed'));
  },

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  },

  // Handle form-based cart additions from Alpine.js
  addFromForm(form) {
    if (!form) return;
    return this.addToCart(form);
  },

  /**
   * Validate required options before adding to cart
   * Prevents unnecessary server request when options are missing
   */
  validateAndAdd(form) {
    if (!form) return;
    if (!form.checkValidity()) {
      form.reportValidity();
      this.highlightMissingOptions(form);
      return;
    }
    return this.addToCart(form);
  },

  /**
   * Buy Now — validate, add to cart, redirect to checkout
   * @param {HTMLFormElement} form
   * @param {string} checkoutUrl - checkout page URL
   */
  async buyNow(form, checkoutUrl) {
    if (!form) return;

    // Client-side validation (only relevant for forms with options)
    if (!form.checkValidity()) {
      form.reportValidity();
      this.highlightMissingOptions(form);
      return;
    }

    const formData = new FormData(form);
    formData.append('add_cart_product', 'true');

    if (window.Alpine && window.Alpine.store('loader')) {
      window.Alpine.store('loader').show('buy-now');
    }

    try {
      const response = await fetch(this.cartUrl, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cartData = await response.json();

      // Check for server-side errors (out of stock, etc.)
      if (cartData.alert) {
        if (window.Alpine && window.Alpine.store('loader')) {
          window.Alpine.store('loader').hide('buy-now');
        }
        if (window.Notification) {
          window.Notification.error(cartData.alert, 4000);
        }
        return;
      }

      // Update count (handles both POST and GET response formats)
      this.itemCount = this.parseCartCount(cartData);
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('[Cart] buyNow failed:', error);
      if (window.Alpine && window.Alpine.store('loader')) {
        window.Alpine.store('loader').hide('buy-now');
      }
      if (window.Notification) {
        window.Notification.error(error.message, 5000);
      }
    }
  },

  /**
   * Update item quantity via AJAX
   * @param {string} itemKey - Cart item key
   * @param {number} newQuantity - New quantity value
   */
  async updateQuantity(itemKey, newQuantity) {
    const loaderId = 'cart-qty-' + itemKey;

    try {
      // Show loader
      if (window.Alpine && window.Alpine.store('loader')) {
        window.Alpine.store('loader').show(loaderId, window.tx('loadingUpdating', 'Updating...'));
      }

      // Use server's standard update_cart_item
      // Server expects: item[<key>][quantity] = <value>
      const formData = new FormData();
      formData.append('update_cart_item', itemKey);
      formData.append(`item[${itemKey}][quantity]`, newQuantity);

      // POST to cart.json — single lightweight request
      const response = await fetch(this.cartUrl, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cartData = await response.json();

      if (cartData.alert) {
        if (window.Notification) {
          window.Notification.warning(cartData.alert, 4000);
        }
      }

      // Update count (handles both POST and GET response formats)
      this.itemCount = this.parseCartCount(cartData);
      document.dispatchEvent(new CustomEvent('cart-updated', { detail: cartData }));

      // Fetch full cart data for sidebar update (POST may not include items)
      await this.refreshCart();

      if (window.Notification && !cartData.alert) {
        window.Notification.success(window.tx('notificationCartUpdated', 'Cart updated'), 2000);
      }
    } catch (error) {
      console.error('[Cart] updateQuantity failed:', error);
      if (window.Notification) {
        window.Notification.error(window.tx('errorUpdateFailed', 'Failed to update'), 3000);
      }
    } finally {
      // Hide loader
      if (window.Alpine && window.Alpine.store('loader')) {
        window.Alpine.store('loader').hide(loaderId);
      }
    }
  },

  /**
   * Remove item from cart via AJAX
   */
  async removeItem(itemKey) {
    const loaderId = 'cart-remove-' + itemKey;

    try {
      // Send Analytics event BEFORE removal
      if (this.analyticsEnabled && window.Analytics) {
        const itemElement = document.querySelector(`[data-item-key="${itemKey}"]`);
        if (itemElement) {
          window.Analytics.removeFromCart(itemElement);
        }
      }

      // Show loader
      if (window.Alpine && window.Alpine.store('loader')) {
        window.Alpine.store('loader').show(loaderId, window.tx('loadingRemoving', 'Removing...'));
      }

      const formData = new FormData();
      formData.append('remove_cart_item', itemKey);

      // POST to cart.json — single lightweight request
      const response = await fetch(this.cartUrl, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cartData = await response.json();

      // Update count (handles both POST and GET response formats)
      this.itemCount = this.parseCartCount(cartData);
      document.dispatchEvent(new CustomEvent('cart-updated', { detail: cartData }));

      // Fetch full cart data for sidebar update (POST may not include items)
      await this.refreshCart();

      if (window.Notification) {
        window.Notification.success(window.tx('notificationItemRemoved', 'Item removed'), 2000);
      }
    } catch (error) {
      console.error('[Cart] removeItem failed:', error);
      if (window.Notification) {
        window.Notification.error(window.tx('errorRemoveFailed', 'Failed to remove'), 3000);
      }
    } finally {
      // Hide loader
      if (window.Alpine && window.Alpine.store('loader')) {
        window.Alpine.store('loader').hide(loaderId);
      }
    }
  },

  /**
   * Update sidebar DOM directly from cart JSON data (zero extra network requests)
   * Called after addToCart, updateQuantity, removeItem with the already-fetched cartData.
   * Both filled and empty states always exist in DOM (x-show), so no template timing issues.
   */
  updateSidebarFromJson(cartData) {
    try {
      const component = document.querySelector('.cart-component');
      if (!component) return;

      const itemsContainer = component.querySelector('[data-cart-items]');
      const subtotalEl = component.querySelector('[data-cart-subtotal]');
      const items = cartData.items || [];

      // Update subtotal
      if (subtotalEl) {
        subtotalEl.textContent = cartData.subtotal?.formatted || '';
      }

      // Update free shipping progress
      this.updateFreeShippingBar(component, cartData.subtotal?.value || 0);

      if (!itemsContainer) return;

      // Build a map of current DOM items by key
      const currentItems = itemsContainer.querySelectorAll('.cart-item[data-item-key]');
      const currentKeys = new Set();
      currentItems.forEach(el => currentKeys.add(el.dataset.itemKey));

      // Build a map of new items by key
      const newKeys = new Set(items.map(i => i.key));

      // Remove items no longer in cart
      currentItems.forEach(el => {
        if (!newKeys.has(el.dataset.itemKey)) {
          el.style.transition = 'opacity 0.3s, max-height 0.3s';
          el.style.opacity = '0';
          el.style.maxHeight = '0';
          el.style.overflow = 'hidden';
          setTimeout(() => el.remove(), 300);
        }
      });

      // Update existing items & add new ones
      items.forEach(item => {
        const existing = itemsContainer.querySelector(`.cart-item[data-item-key="${CSS.escape(item.key)}"]`);

        if (existing) {
          // Update quantity in Alpine.js scope
          const alpineData = existing._x_dataStack?.[0];
          if (alpineData) {
            alpineData.quantity = item.quantity;
          }

          // Update line price display
          const priceEl = existing.querySelector('[data-item-price]');
          if (priceEl) {
            priceEl.textContent = this.formatPrice(item.price * item.quantity, component);
          }
        } else {
          // New item — build and insert before the recently-viewed section
          const newItemHtml = this.buildCartItemHtml(item, component);
          const recentlyViewed = itemsContainer.querySelector('[data-cart-recently-viewed]');

          const temp = document.createElement('div');
          temp.innerHTML = newItemHtml;
          const newEl = temp.firstElementChild;

          if (recentlyViewed) {
            itemsContainer.insertBefore(newEl, recentlyViewed);
          } else {
            itemsContainer.appendChild(newEl);
          }

          // Initialize Alpine.js on the new element
          if (window.Alpine) {
            window.Alpine.initTree(newEl);
          }
        }
      });

      // Empty/filled state toggle is handled by Alpine x-show on $store.cart.itemCount
    } catch (error) {
      console.error('[Cart] updateSidebarFromJson failed:', error);
    }
  },

  /**
   * Update the free shipping progress bar
   */
  updateFreeShippingBar(component, subtotalValue) {
    const threshold = parseFloat(component.dataset.freeShippingThreshold) || 0;
    if (threshold <= 0) return;

    const remaining = Math.max(0, threshold - subtotalValue);
    const reached = subtotalValue >= threshold;
    const percentage = Math.min(100, (subtotalValue / threshold) * 100);

    // Toggle reached/not-reached states
    const notReachedEl = component.querySelector('[data-shipping-not-reached]');
    const reachedEl = component.querySelector('[data-shipping-reached]');
    const trackEl = component.querySelector('[data-shipping-track]');

    if (notReachedEl) notReachedEl.style.display = reached ? 'none' : '';
    if (reachedEl) reachedEl.style.display = reached ? '' : 'none';
    if (trackEl) trackEl.style.display = reached ? 'none' : '';

    // Update progress bar
    const progressBar = component.querySelector('[data-shipping-progress]');
    if (progressBar) {
      progressBar.style.width = percentage + '%';
    }

    // Update remaining amount text
    const remainingEl = component.querySelector('[data-shipping-remaining]');
    if (remainingEl) {
      remainingEl.textContent = remaining > 0 ? this.formatPrice(remaining, component) : '';
    }
  },

  /**
   * Format a price value using currency prefix/suffix from component data attributes
   */
  formatPrice(value, component) {
    const before = component?.dataset.currencyBefore || '';
    const after = component?.dataset.currencyAfter || '';
    // Use locale-aware number formatting (2 decimal places)
    const formatted = Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return before + formatted + after;
  },

  /**
   * Build HTML for a new cart item from JSON data
   */
  buildCartItemHtml(item, component) {
    const aspectRatio = component?.dataset.productAspectRatio || '2/3';
    const safeKey = item.key.replace(/'/g, "\\'").replace(/"/g, '&quot;');
    const safeName = (item.name || '').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const lineTotal = this.formatPrice(item.price * item.quantity, component);

    // Build options HTML
    let optionsHtml = '';
    if (item.options && typeof item.options === 'object') {
      const values = [];
      for (const [, optVal] of Object.entries(item.options)) {
        const val = typeof optVal === 'object' ? (optVal.value || '') : optVal;
        const str = Array.isArray(val) ? val.join(', ') : String(val);
        if (str) values.push(`<span>${str.replace(/</g, '&lt;')}</span>`);
      }
      if (values.length) {
        optionsHtml = `<div class="text-xs text-[var(--color-text-tertiary)] mb-2">${values.join(' ')}</div>`;
      }
    }

    return `
      <div class="cart-item flex gap-4 p-6 border-b border-[var(--color-border)]" data-item-key="${safeKey}" x-data="{ quantity: ${item.quantity} }">
        <a href="${item.link || '#'}" class="flex-shrink-0 w-20 bg-[var(--color-bg)]" style="aspect-ratio: ${aspectRatio};">
          <img src="${item.thumbnail || ''}" alt="${safeName}" class="w-full h-full object-cover" loading="lazy">
        </a>
        <div class="flex-1 min-w-0">
          <h3 class="text-sm font-medium text-[var(--color-text-primary)] leading-tight mb-1">
            <a href="${item.link || '#'}" class="hover:text-[var(--color-accent)] transition-colors">${safeName}</a>
          </h3>
          ${optionsHtml}
          <div class="text-sm font-semibold text-[var(--color-text-primary)] mb-3" data-item-price>${lineTotal}</div>
          <div class="flex items-center justify-between">
            <div class="flex items-center border border-[var(--color-border)]">
              <button type="button"
                      @click="quantity > 1 && $store.cart.updateQuantity('${safeKey}', quantity - 1)"
                      :disabled="quantity <= 1"
                      class="w-8 h-8 flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] disabled:opacity-30 transition-colors">
                <svg class="w-4 h-4"><use href="#icon-minus"></use></svg>
              </button>
              <span class="w-10 text-center text-sm font-medium text-[var(--color-text-primary)]" x-text="quantity"></span>
              <button type="button"
                      @click="$store.cart.updateQuantity('${safeKey}', quantity + 1)"
                      class="w-8 h-8 flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors">
                <svg class="w-4 h-4"><use href="#icon-plus"></use></svg>
              </button>
            </div>
            <button type="button"
                    @click="$store.cart.removeItem('${safeKey}')"
                    class="text-xs text-[var(--color-text-tertiary)] hover:text-[var(--color-accent)] transition-colors uppercase tracking-wider">
              ${window.tx?.('titleRemove', 'Remove') || 'Remove'}
            </button>
          </div>
        </div>
      </div>`;
  },
};
